import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Edit, Plus, ChevronDown, X, Leaf } from 'lucide-react';
import { cn } from '../../../../lib/utils';
import {
  primaryText,
  secondaryText,
  smallTextSize,
} from '../../../../styling/stylingHelper';
import { DIETS, toTranslationKey } from '../../../../types/RecipeEnums';

interface RecipeDietsSectionProps {
  diets: string[];
  onSave: (diets: string[]) => Promise<void>;
}

const RecipeDietsSection = ({ diets: initialDiets, onSave }: RecipeDietsSectionProps) => {
  const { t } = useTranslation();
  const dietsDropdownRef = useRef<HTMLDivElement>(null);

  const [isEditing, setIsEditing] = useState(false);
  const [diets, setDiets] = useState<string[]>(initialDiets);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  // Sync with prop changes
  useEffect(() => {
    setDiets(initialDiets);
  }, [initialDiets]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dietsDropdownRef.current && !dietsDropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleToggleDiet = (diet: string) => {
    if (diets.includes(diet)) {
      setDiets(diets.filter(d => d !== diet));
    } else {
      setDiets([...diets, diet]);
    }
  };

  const handleRemoveDiet = (diet: string) => {
    setDiets(diets.filter((d) => d !== diet));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await onSave(diets);
      setIsEditing(false);
      setIsDropdownOpen(false);
    } catch (error) {
      console.error('Failed to save diets:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setDiets(initialDiets);
    setIsDropdownOpen(false);
    setIsEditing(false);
  };

  // Show empty state with add button
  if (initialDiets.length === 0 && !isEditing) {
    return (
      <div className={cn(
        "bg-white dark:bg-background-dark rounded-xl border border-[#E5E7EB] dark:border-borderPrimary-dark",
        "p-4 h-full"
      )}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Leaf className="w-4 h-4 text-[#0E8965] dark:text-[#0E8965]" />
            <h2 className={cn("text-[16px] leading-6 font-semibold", primaryText)}>
              {t('recipes.diets')}
            </h2>
          </div>
          <button
            onClick={() => setIsEditing(true)}
            className={cn(
              "p-2 rounded-lg",
              "text-[#475467] dark:text-textSecondary-dark",
              "hover:bg-[#F2F4F7] dark:hover:bg-zinc-800 transition-colors"
            )}
          >
            <Plus className="w-4 h-4" />
          </button>
        </div>
        <p className={cn(smallTextSize, secondaryText, 'mt-3')}>{t('recipes.noDietsListed')}</p>
      </div>
    );
  }

  return (
    <div className={cn(
      "bg-white dark:bg-background-dark rounded-xl border border-[#E5E7EB] dark:border-borderPrimary-dark",
      "p-4 h-full"
    )}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Leaf className="w-4 h-4 text-[#0E8965] dark:text-[#0E8965]" />
          <h2 className={cn("text-[16px] leading-6 font-semibold", primaryText)}>
            {t('recipes.diets')}
          </h2>
        </div>
        {!isEditing && (
          <button
            onClick={() => setIsEditing(true)}
            className={cn(
              "p-2 rounded-lg",
              "text-[#475467] dark:text-textSecondary-dark",
              "hover:bg-[#F2F4F7] dark:hover:bg-zinc-800 transition-colors"
            )}
          >
            <Edit className="w-4 h-4" />
          </button>
        )}
      </div>

      {isEditing ? (
        <div>
          {/* Multi-select dropdown */}
          <div className="relative mb-3" ref={dietsDropdownRef}>
            <button
              type="button"
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              className={cn(
                "w-full px-4 py-2.5 rounded-xl",
                "border border-[#D0D5DD] dark:border-borderPrimary-dark",
                "bg-white dark:bg-background-dark",
                "text-[14px] leading-5",
                "flex items-center justify-between",
                "hover:border-[#98A2B3] dark:hover:border-zinc-600 transition-colors"
              )}
            >
              <span className={diets.length === 0 ? 'text-[#667085] dark:text-textTertiary-dark' : 'text-[#344054] dark:text-textSecondary-dark'}>
                {diets.length === 0 ? t('recipes.selectDiets') : `${diets.length} ${t('recipes.selected')}`}
              </span>
              <ChevronDown className={cn(
                'w-4 h-4 text-[#667085] dark:text-textTertiary-dark transition-transform',
                isDropdownOpen && 'rotate-180'
              )} />
            </button>

            {isDropdownOpen && (
              <div className={cn(
                "absolute z-10 w-full mt-1",
                "bg-white dark:bg-background-dark",
                "border border-[#E5E7EB] dark:border-borderPrimary-dark rounded-xl",
                "shadow-lg max-h-48 overflow-y-auto"
              )}>
                {DIETS.map((diet) => (
                  <label
                    key={t(`dietKeys.${toTranslationKey(diet)}`, diet)}
                    className={cn(
                      "flex items-center gap-3 px-4 py-2.5",
                      "hover:bg-[#F9FAFB] dark:hover:bg-zinc-800 cursor-pointer",
                      "first:rounded-t-xl last:rounded-b-xl"
                    )}
                  >
                    <input
                      type="checkbox"
                      checked={diets.includes(diet)}
                      onChange={() => handleToggleDiet(diet)}
                      className={cn(
                        "w-4 h-4 rounded border-[#D0D5DD] dark:border-zinc-600",
                        "text-[#0E8965] dark:text-[#0E8965] focus:ring-[#0E8965]/20 dark:focus:ring-[#0E8965]/20"
                      )}
                    />
                    <span className="text-sm text-[#344054] dark:text-textSecondary-dark">{t(`dietKeys.${toTranslationKey(diet)}`, diet)}</span>
                  </label>
                ))}
              </div>
            )}
          </div>

          {/* Selected diets tags */}
          {diets.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-3">
              {diets.map((diet, index) => (
                <span
                  key={index}
                  className={cn(
                    "inline-flex items-center gap-1.5 px-2.5 py-1",
                    "bg-[#ECFDF3] text-[#027A48] dark:bg-green-900/20 dark:text-green-400",
                    "rounded-lg text-sm font-medium"
                  )}
                >
                  {t(`dietKeys.${toTranslationKey(diet)}`, diet)}
                  <button
                    onClick={() => handleRemoveDiet(diet)}
                    className="hover:text-[#05603A] dark:hover:text-green-300"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </span>
              ))}
            </div>
          )}

          <div className="flex gap-2">
            <button
              onClick={handleCancel}
              disabled={saving}
              className={cn(
                "px-4 py-2 rounded-xl",
                "border border-[#D0D5DD] dark:border-borderPrimary-dark",
                "text-[14px] leading-5 font-semibold",
                "text-[#344054] dark:text-textSecondary-dark",
                "hover:bg-[#F9FAFB] dark:hover:bg-backgroundDarker-dark transition-colors",
                "disabled:opacity-50"
              )}
            >
              {t('common.cancel')}
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className={cn(
                "px-4 py-2 rounded-xl",
                "bg-[#0E8965] dark:bg-[#0E8965] text-white dark:text-white",
                "text-[14px] leading-5 font-semibold",
                "hover:bg-[#047857] transition-colors",
                "disabled:opacity-50"
              )}
            >
              {saving ? t('common.saving') : t('common.save')}
            </button>
          </div>
        </div>
      ) : (
        <div className="flex flex-wrap gap-2">
          {diets.map((diet, index) => (
            <span
              key={index}
              className={cn(
                "px-2.5 py-1 rounded-lg text-sm font-medium",
                "bg-[#ECFDF3] text-[#027A48] dark:bg-green-900/20 dark:text-green-400"
              )}
            >
              {t(`dietKeys.${toTranslationKey(diet)}`, diet)}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

export default RecipeDietsSection;
